import { useState } from 'react';

const TeamCard = ({ name, title, description, image }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 hover-lift hover-border cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] overflow-hidden">
        {/* Team Member Photo */}
        {image && !imageError ? (
          <div className="absolute inset-0 w-full h-full overflow-hidden">
            <img
              src={image}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          /* Placeholder with Initials */
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-light via-primary to-accent">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-2xl group-hover:scale-110 transition-transform duration-500">
              {name ? name.split(' ').map(n => n[0]).join('') : 'TA'}
            </div>
          </div>
        )}
        
        {/* Subtle overlay for better text readability on hover */}
        {image && !imageError && (
          <div
            className={`absolute inset-0 bg-gradient-to-br from-primary/60 via-accent/60 to-primary/60 transition-all duration-500 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}
        
        {/* Hover Overlay with Gradient - Only when image is not available */}
        {(!image || imageError) && (
          <div
            className={`absolute inset-0 bg-gradient-to-br from-accent/90 via-primary/80 to-accent/90 transition-all duration-500 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}
        
        {/* Hover Content with Animation */}
        <div
          className={`absolute inset-0 flex items-center justify-center p-6 transition-all duration-500 ${
            isHovered ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
          }`}
        >
          <p className="text-white text-center text-sm leading-relaxed font-medium animate-appear-center">
            {description}
          </p>
        </div>

        {/* Floating Badge */}
        <div className={`absolute top-3 right-3 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-semibold text-white transition-all duration-300 ${
          isHovered ? 'opacity-0 scale-50' : 'opacity-100 scale-100'
        }`}>
          Advisor
        </div>
      </div>

      {/* Content */}
      <div className="p-6 relative bg-white dark:bg-neutral-800 transition-colors duration-300">
        <h3 className="text-xl font-semibold text-primary dark:text-white mb-1 group-hover:text-accent transition-colors duration-300">
          {name}
        </h3>
        <p className="text-sm text-accent font-medium mb-2 group-hover:scale-105 transform transition-transform duration-300 origin-left">
          {title}
        </p>
        
        {/* Description with smooth transition */}
        <p className={`text-sm text-neutral-600 dark:text-neutral-300 transition-all duration-500 ${
          isHovered ? 'opacity-0 h-0 mt-0' : 'opacity-100 h-auto mt-2'
        }`}>
          {description.substring(0, 80)}{description.length > 80 ? '...' : ''}
        </p>

        {/* View More Indicator */}
        <div className={`mt-2 text-xs text-accent font-semibold transition-all duration-300 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}>
          View Full Profile →
        </div>
      </div>

      {/* Corner Accent with Animation */}
      <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-accent to-primary transition-all duration-500 ${
        isHovered ? 'opacity-100 scale-150 rotate-45' : 'opacity-0 scale-50'
      }`} style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }} />

      {/* Shine Effect on Hover */}
      <div className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${
        isHovered ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shine" />
      </div>
    </div>
  );
};

export default TeamCard;
